import useBreakpoints from "@kamae-apps/shared/Hooks/useMediaQuery"
import { FC, ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react"

export enum SidebarMode {
  MOBILE = "mobile",
  COLLAPSED = "collapsed",
  EXPANDED = "expanded",
}

type SidebarModeContextValue = {
  mode: SidebarMode
  viewportMode: SidebarMode
  setOverride: (override: SidebarMode | null) => void
}

const SidebarModeContext = createContext<SidebarModeContextValue | null>(null)

export const SidebarModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isMobile, isDesktop } = useBreakpoints()
  const [override, setOverride] = useState<SidebarMode | null>(null)

  const viewportMode = isMobile ? SidebarMode.MOBILE : isDesktop ? SidebarMode.EXPANDED : SidebarMode.COLLAPSED
  const mode = override ?? viewportMode

  const value = useMemo(() => ({ mode, viewportMode, setOverride }), [mode, viewportMode])

  return <SidebarModeContext.Provider value={value}>{children}</SidebarModeContext.Provider>
}

export const useSidebarMode = (): SidebarModeContextValue => {
  const ctx = useContext(SidebarModeContext)
  if (!ctx) {
    throw new Error("useSidebarMode must be used within a SidebarModeProvider")
  }
  return ctx
}

const MODE_ORDER: Record<SidebarMode, number> = {
  [SidebarMode.MOBILE]: 0,
  [SidebarMode.COLLAPSED]: 1,
  [SidebarMode.EXPANDED]: 2,
}

export const useSidebarOverride = (target: SidebarMode) => {
  const { viewportMode, setOverride } = useSidebarMode()

  useEffect(() => {
    if (MODE_ORDER[target] < MODE_ORDER[viewportMode]) {
      setOverride(target)
    }
    return () => setOverride(null)
  }, [target, viewportMode, setOverride])
}
